/**
 * mod-sgtm-tracking-attributes.ts
 *
 * Persiste os identificadores de sessão do GA4 + fingerprint como cart
 * attributes da Shopify. Quando o pedido é criado, esses atributos chegam
 * no webhook order/created e o orquestrador consegue atrelar a order à
 * sessão de tracking — mesmo que o pixel falhe no checkout.
 *
 * Atributos escritos (sufixo `__` = privado, oculto do Liquid mas visível
 * no Admin API e webhooks):
 *   sgtm_ga_session_id__       — GS\d.\d.<id>           (de _ga_<MEASUREMENT_ID>)
 *   sgtm_ga_session_number__   — GS\d.\d.<id>.<n>       (de _ga_<MEASUREMENT_ID>)
 *   sgtm_client_id__           — <random>.<ts>          (gtag('get', ..., 'client_id'))
 *   sgtm_fingerprint_id__      — <uuid>                 (localStorage fingerprintPro)
 *
 * Padrão idêntico ao mod-cart-attribute-tracker: escuta INSIDER_CART_CHANGED,
 * só persiste quando algo muda, sem loops via silentCartUpdate.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { INSIDER_CART_CHANGED, silentCartUpdate, } from './mod-cart-event-bus.js';
// ─── Constantes ───────────────────────────────────────────────────────────────
const MEASUREMENT_ID = 'G-5ZRTYWCGE8';
const SESSION_COOKIE_NAME = `_ga_${MEASUREMENT_ID.replace(/^G-/, '')}`;
// Formato do cookie de sessão: GS\d.\d.<session_id>.<session_number>.<...>
// Exemplo real: GS2.1.s1775114885$o3$g1$t1775114920$j25$l0$h0
const SESSION_COOKIE_RE = /^GS\d+\.\d+\.s?(\d+)(?:\$o(\d+))?(?:\.(\d+))?/;
const CLIENT_ID_TIMEOUT_MS = 2000;
// Chaves dos atributos no carrinho
const ATTR_KEYS = {
    sessionId: 'sgtm_ga_session_id__',
    sessionNumber: 'sgtm_ga_session_number__',
    clientId: 'sgtm_client_id__',
    fingerprintId: 'sgtm_fingerprint_id__',
};
// ─── Helpers de leitura ───────────────────────────────────────────────────────
function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? match[1] : null;
}
function readSessionAttrs() {
    const raw = readCookie(SESSION_COOKIE_NAME);
    if (!raw)
        return {};
    const m = raw.match(SESSION_COOKIE_RE);
    if (!m)
        return {};
    const [, sessionId, sessionNumberDollar, sessionNumberDot] = m;
    const sessionNumber = sessionNumberDollar !== null && sessionNumberDollar !== void 0 ? sessionNumberDollar : sessionNumberDot;
    const out = {};
    if (sessionId)
        out[ATTR_KEYS.sessionId] = sessionId;
    if (sessionNumber)
        out[ATTR_KEYS.sessionNumber] = sessionNumber;
    return out;
}
function readFingerprintId() {
    try {
        const raw = localStorage.getItem('fingerprintPro');
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return typeof parsed.visitorId === 'string' ? parsed.visitorId : null;
    }
    catch (_a) {
        return null;
    }
}
// gtag('get', ..., 'client_id', cb) é a forma oficial de obter o cid que vai
// no request — funciona com cookie_domain auto, com server-managed FPID, etc.
// Fallback: regex no _ga se o gtag não respondeu (ex: bloqueado por ad blocker).
function readClientId() {
    return new Promise((resolve) => {
        const w = window;
        if (typeof w.gtag !== 'function') {
            resolve(readClientIdFromCookie());
            return;
        }
        const timer = setTimeout(() => resolve(readClientIdFromCookie()), CLIENT_ID_TIMEOUT_MS);
        try {
            w.gtag('get', MEASUREMENT_ID, 'client_id', (cid) => {
                clearTimeout(timer);
                resolve(cid || readClientIdFromCookie());
            });
        }
        catch (_a) {
            clearTimeout(timer);
            resolve(readClientIdFromCookie());
        }
    });
}
function readClientIdFromCookie() {
    const raw = readCookie('_ga');
    if (!raw)
        return null;
    const m = raw.match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
    return m ? m[1] : null;
}
// ─── Resolver com cache em memória ────────────────────────────────────────────
// Os 4 valores são estáveis durante a sessão. Resolve uma vez e cacheia.
// gtag('get') é assíncrono — singleton evita chamadas concorrentes.
let _attrsPromise = null;
function resolveAttrs() {
    if (_attrsPromise)
        return _attrsPromise;
    _attrsPromise = (() => __awaiter(this, void 0, void 0, function* () {
        const sessionAttrs = readSessionAttrs();
        const clientId = yield readClientId();
        const fingerprintId = readFingerprintId();
        const attrs = Object.assign({}, sessionAttrs);
        if (clientId)
            attrs[ATTR_KEYS.clientId] = clientId;
        if (fingerprintId)
            attrs[ATTR_KEYS.fingerprintId] = fingerprintId;
        return attrs;
    }))();
    return _attrsPromise;
}
// ─── Persistência ─────────────────────────────────────────────────────────────
// Comparamos contra o estado atual do cart pra decidir se precisa POSTar.
// Evita request desnecessário quando nada mudou — Shopify cobra latência.
function diffNeeded(current, desired) {
    const diff = {};
    for (const [k, v] of Object.entries(desired)) {
        if (!v)
            continue;
        if (current[k] !== v)
            diff[k] = v;
    }
    return diff;
}
let _persisting = false;
function persistAttrs(currentAttributes) {
    return __awaiter(this, void 0, void 0, function* () {
        if (_persisting)
            return;
        _persisting = true;
        try {
            const desired = yield resolveAttrs();
            const diff = diffNeeded(currentAttributes, desired);
            if (Object.keys(diff).length === 0)
                return;
            console.debug('[sgtm-tracking-attributes] persisting', diff);
            yield silentCartUpdate({ attributes: diff });
        }
        catch (err) {
            console.warn('[sgtm-tracking-attributes] failed to persist:', err);
        }
        finally {
            _persisting = false;
        }
    });
}
document.addEventListener(INSIDER_CART_CHANGED, (e) => {
    var _a;
    const { cart } = e.detail;
    if (!cart)
        return;
    persistAttrs((_a = cart.attributes) !== null && _a !== void 0 ? _a : {});
});
